import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Card,
  List,
  ListItem,
  Divider,
  Fade
} from '@mui/material';
import {
  Event as EventIcon,
  LocalHospital as LocalHospitalIcon,
  PlayArrow as PlayArrowIcon,
  VolumeUp as VolumeUpIcon,
  Wash as WashIcon,
  FaceRetouchingNatural as FaceMaskIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import AcessoRestrito from '../../components/AcessoRestrito';
import api from '../../services/api';
import env from '../../config/env.js';
import { useSocket } from '../../contexts/SocketContext';
import { format, parseISO, isToday, isFuture, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { normalizarUrlVideo } from './videoUtils';

function eDoDiaAtual(horario) {
  if (!horario) return false;
  const data = new Date(horario);
  return !Number.isNaN(data.getTime()) && isToday(data);
}

const normalizarIdiomaVoz = (idioma) => String(idioma || '').replace('_', '-').toLowerCase();

// A propriedade `lang` é apenas uma preferência para o navegador. A voz precisa
// ser escolhida explicitamente para impedir fallback para a voz padrão do SO.
const encontrarVozPtBr = (vozes = []) => vozes.find((voz) => normalizarIdiomaVoz(voz.lang) === 'pt-br') || null;

const aguardarVozPtBr = () => new Promise((resolve) => {
  const sintese = window.speechSynthesis;
  const vozAtual = encontrarVozPtBr(sintese.getVoices());
  if (vozAtual) {
    resolve(vozAtual);
    return;
  }

  let finalizado = false;
  let timeout;
  const finalizar = (voz) => {
    if (finalizado) return;
    finalizado = true;
    clearTimeout(timeout);
    sintese.removeEventListener('voiceschanged', aoAlterarVozes);
    resolve(voz || null);
  };
  const aoAlterarVozes = () => finalizar(encontrarVozPtBr(sintese.getVoices()));

  sintese.addEventListener('voiceschanged', aoAlterarVozes);
  // Safari pode carregar as vozes depois da abertura da página. Nunca usamos a
  // voz padrão caso a brasileira não seja disponibilizada nesse intervalo.
  timeout = setTimeout(() => finalizar(encontrarVozPtBr(sintese.getVoices())), 2500);
});

/**
 * Painel de Detalhes de Atendimento - Design Sala de Espera
 * 
 * Painel moderno inspirado em sistema de informação para sala de espera
 */
function PainelDetalhes() {
  const { socket } = useSocket();
  const testeId = useMemo(() => new URLSearchParams(window.location.search).get('teste'), []);
  const [dadosTeste] = useState(() => {
    if (!testeId) return null;
    try {
      const chaveTeste = `painelTeste:${testeId}`;
      const dados = JSON.parse(localStorage.getItem(chaveTeste) || 'null');
      localStorage.removeItem(chaveTeste);
      return dados;
    } catch (_) {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [agendamentos, setAgendamentos] = useState([]);
  const [atendimentos, setAtendimentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [locais, setLocais] = useState([]);
  const [dataHoraAtual, setDataHoraAtual] = useState(new Date());
  const [filaAtendimento, setFilaAtendimento] = useState([]);
  const [atendimentosAtivos, setAtendimentosAtivos] = useState([]);
  
  // Configurações do painel
  const [configuracoes, setConfiguracoes] = useState({
    textoTitulo: 'Painel de Atendimento',
    textoSubtitulo: '',
    videoUrl: '',
    somAtivado: true,
    somSelecionado: 'beep',
    autoRefresh: true,
    refreshInterval: 30
  });
  
  // Refs para evitar piscar durante atualizações
  const filaAnteriorRef = useRef([]);
  const chamadasAtivasAnteriorRef = useRef([]);
  const locucaoAtualRef = useRef(0);

  // Carregar configurações do backend
  useEffect(() => {
    const carregarConfiguracoesBackend = async () => {
      try {
        const token = localStorage.getItem("token");
        const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");

        if (!token || !codHashEmpresa) {
          return;
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'x-second-key': env.SECOND_SECRET_KEY,
          'x-codhash-empresa': codHashEmpresa
        };

        const response = await api.get('/painel/configuracoes', { headers });
        
        if (response.data.success && response.data.data) {
          setConfiguracoes(prev => ({ ...prev, ...response.data.data }));
        }
      } catch (error) {
        console.error('Erro ao carregar configurações do backend:', error);
        // Fallback: tentar carregar do localStorage
        try {
          const configSalva = localStorage.getItem('painelConfiguracoes');
          if (configSalva) {
            const config = JSON.parse(configSalva);
            setConfiguracoes(prev => ({ ...prev, ...config }));
          }
        } catch (err) {
          console.error('Erro ao carregar configurações do localStorage:', err);
        }
      }
    };

    // Carregar configurações iniciais do backend
    carregarConfiguracoesBackend();

    // Ouvir evento de atualização de configurações (para atualizações em tempo real)
    const handleConfigUpdate = (event) => {
      if (event.detail) {
        setConfiguracoes(prev => ({ ...prev, ...event.detail }));
      }
    };

    const handleConfigStorageUpdate = (event) => {
      if (event.key !== 'painelConfiguracoes' || !event.newValue) return;

      try {
        const config = JSON.parse(event.newValue);
        setConfiguracoes(prev => ({ ...prev, ...config }));
      } catch (error) {
        console.error('Erro ao aplicar configurações atualizadas do painel:', error);
      }
    };

    window.addEventListener('painelConfiguracoesAtualizadas', handleConfigUpdate);
    window.addEventListener('storage', handleConfigStorageUpdate);

    return () => {
      window.removeEventListener('painelConfiguracoesAtualizadas', handleConfigUpdate);
      window.removeEventListener('storage', handleConfigStorageUpdate);
    };
  }, []);

  // Função auxiliar para comparar se a fila mudou significativamente
  const filaMudou = useCallback((filaNova, filaAntiga) => {
    if (filaNova.length !== filaAntiga.length) return true;
    
    // Comparar os primeiros 5 itens (mais importantes para exibição)
    const itensComparar = Math.min(5, filaNova.length);
    for (let i = 0; i < itensComparar; i++) {
      const novo = filaNova[i];
      const antigo = filaAntiga[i];
      if (!antigo || novo.senha !== antigo.senha || novo.cliente !== antigo.cliente) {
        return true;
      }
    }
    return false;
  }, []);

  // Anuncia senha, paciente e local após uma nova chamada.
  const falarSenha = useCallback((senha, chamada = {}) => {
    const identificadorLocucao = locucaoAtualRef.current + 1;
    locucaoAtualRef.current = identificadorLocucao;

    if (!senha) {
      console.warn('[FALAR SENHA] Senha não fornecida');
      return;
    }
    
    if (!window.speechSynthesis) {
      console.warn('[FALAR SENHA] Speech Synthesis não disponível no navegador');
      return;
    }
    
    // Converter senha para string e remover caracteres não numéricos
    const senhaString = String(senha).replace(/\D/g, '');
    if (!senhaString) {
      console.warn('[FALAR SENHA] Senha não contém dígitos:', senha);
      return;
    }
    
    // Mapear dígitos para palavras em português
    const digitos = {
      '0': 'zero',
      '1': 'um',
      '2': 'dois',
      '3': 'três',
      '4': 'quatro',
      '5': 'cinco',
      '6': 'seis',
      '7': 'sete',
      '8': 'oito',
      '9': 'nove'
    };
    
    const senhaPorExtenso = senhaString.split('').map(digito => digitos[digito] || digito).join(', ');
    const cliente = String(chamada.cliente || '').trim();
    const local = String(chamada.local || '').trim();
    const complementos = [];
    if (cliente) complementos.push(`${cliente}, por favor`);
    if (local) complementos.push(`dirija-se ao ${local}`);
    const texto = [`Senha ${senhaPorExtenso}`, ...complementos].join('. ');
    
    // ✅ SEGURANÇA: Não logar senha em texto claro
    console.log('[FALAR SENHA] Preparando fala de senha (***REDACTED***)');
    
    // Cancelar qualquer síntese de voz anterior
    window.speechSynthesis.cancel();
    
    // Aguarda a voz brasileira ser carregada pelo navegador antes de falar.
    setTimeout(async () => {
      const vozPtBr = await aguardarVozPtBr();
      if (identificadorLocucao !== locucaoAtualRef.current) return;
      if (!vozPtBr) {
        console.error('[FALAR SENHA] Nenhuma voz pt-BR está instalada ou disponível neste dispositivo. A chamada não será anunciada com outro idioma.');
        return;
      }
      executarFala(texto, vozPtBr);
    }, 50);
    
    function executarFala(textoParaFalar, vozPtBr) {
      // Criar utterance com voz brasileira explicitamente selecionada.
      const utterance = new SpeechSynthesisUtterance(textoParaFalar);
      utterance.lang = 'pt-BR';
      utterance.rate = 0.9; // Velocidade um pouco mais lenta
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      utterance.voice = vozPtBr;
      console.log('[FALAR SENHA] Usando voz pt-BR:', vozPtBr.name, vozPtBr.lang);
      
      // Adicionar handlers de evento para debug
      utterance.onstart = () => console.log('[FALAR SENHA] Iniciando locução da chamada');
      
      utterance.onend = () => {
        console.log('[FALAR SENHA] Fala concluída');
      };
      
      utterance.onerror = (event) => {
        console.error('[FALAR SENHA] Erro ao falar:', event.error);
      };
      
      // Falar a senha
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  // O painel recebe um estado já filtrado pelo tenant no backend; não consulta mais
  // cadastros globais nem a fila Redis diretamente no navegador.
  const carregarDados = useCallback(async (isInitialLoad = false) => {
    if (dadosTeste) {
      const ativos = dadosTeste.ativos || [];
      const proximos = dadosTeste.proximos || [];
      setAtendimentosAtivos(ativos);
      setFilaAtendimento(proximos.map((item, index) => ({
        ...item,
        atendimentoId: item.id,
        timestamp: item.horario,
        key: `${item.senha || index}-${item.id || index}`
      })));
      setAtendimentos([...ativos, ...proximos].map((item) => ({
        id_atendimento: item.id,
        id: item.id,
        ordem_chegada: item.senha,
        dataHoraInicio: item.horario,
        status: item.status
      })));
      setInitialLoading(false);
      return;
    }
    if (isInitialLoad) setLoading(true);
    try {
      const response = await api.get('/painel/estado');
      const estado = response.data || {};
      const ativos = (Array.isArray(estado.ativos) ? estado.ativos : (estado.atual ? [estado.atual] : []))
        .filter((item) => eDoDiaAtual(item.horario));
      const proximos = (Array.isArray(estado.proximos) ? estado.proximos : [])
        .filter((item) => eDoDiaAtual(item.horario));
      setAtendimentosAtivos(ativos);
      const itensExibidos = [...ativos, ...proximos];
      const filaValidada = proximos.map((item, index) => ({
        senha: item.senha,
        cliente: item.cliente,
        colaborador: item.colaborador,
        local: item.local,
        posicaoNaFila: item.posicaoNaFila,
        timestamp: item.horario,
        atendimentoId: item.id,
        key: `${item.senha || index}-${item.id || index}`
      }));
      const todos = [...itensExibidos, ...(estado.ultimasChamadas || []).filter((item) => eDoDiaAtual(item.horario))];
      setAtendimentos(todos.map((item) => ({
        id_atendimento: item.id,
        id: item.id,
        clienteId: item.id,
        colaboradorId: item.colaborador,
        localId: item.local,
        ordem_chegada: item.senha,
        dataHoraInicio: item.horario,
        status: item.status
      })));
      setClientes(todos.map((item) => ({ id: item.id, nome_completo: item.cliente })));
      setColaboradores(todos.map((item) => ({ id: item.colaborador, nome_completo: item.colaborador })));
      setLocais(todos.map((item) => ({ id: item.local, nome: item.local })));
      setAgendamentos([]);
      setServicos([]);

      const idsAtivos = ativos.map((item) => String(item.id));
      const novaChamada = ativos.find((item) => !chamadasAtivasAnteriorRef.current.includes(String(item.id)));

      // Atualização visual nunca depende da reprodução de áudio.
      chamadasAtivasAnteriorRef.current = idsAtivos;
      if (isInitialLoad || filaMudou(filaValidada, filaAnteriorRef.current)) {
        filaAnteriorRef.current = filaValidada;
        setFilaAtendimento(filaValidada);
      }

      if (!isInitialLoad && novaChamada?.senha && configuracoes.somAtivado) {
        try {
          const senhaNova = novaChamada.senha;
            // ✅ SEGURANÇA: Não logar senha em texto claro
            console.log('[PAINEL DETALHES] ✅ Condições atendidas! Tocando som e falando senha (***REDACTED***)');
            // Tocar som baseado na configuração
            const audio = new Audio();
            audio.volume = 0.5;
            
            switch (configuracoes.somSelecionado) {
              case 'beep':
                audio.src = '/sounds/rington1.mp3';
                break;
              case 'notification':
                audio.src = '/sounds/rington2.mp3';
                break;
              case 'bell':
                audio.src = '/sounds/rington3.mp3';
                break;
              case 'chime':
                audio.src = '/sounds/rington4.mp3';
                break;
              default:
                // Beep padrão usando Web Audio API
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.frequency.value = 600;
                osc.type = 'sine';
                gain.gain.setValueAtTime(0.3, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
                osc.start(ctx.currentTime);
                osc.stop(ctx.currentTime + 0.3);
                break;
            }
            
            // O Som de atendimento precisa terminar antes de iniciar a locução.
            if (audio.src) {
              let anuncioRealizado = false;
              let timeoutAnuncio;
              const anunciarChamada = () => {
                if (anuncioRealizado) return;
                anuncioRealizado = true;
                clearTimeout(timeoutAnuncio);
                falarSenha(senhaNova, novaChamada);
              };
              audio.preload = 'auto';
              audio.addEventListener('ended', anunciarChamada, { once: true });
              // Contingência para navegadores/arquivos que não disparem `ended`.
              timeoutAnuncio = setTimeout(anunciarChamada, 8000);
              const reproducao = audio.play();
              Promise.resolve(reproducao).catch(() => {
                audio.removeEventListener('ended', anunciarChamada);
                // Se não conseguir tocar o áudio, falar a senha mesmo assim
                anunciarChamada();
              });
            } else {
              // Se for Web Audio API, esperar um pouco e falar a senha
              setTimeout(() => {
                falarSenha(senhaNova, novaChamada);
              }, 400);
            }
        } catch (audioError) {
          console.error('[PAINEL DETALHES] Erro ao iniciar áudio da chamada:', audioError);
          falarSenha(novaChamada.senha, novaChamada);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
      if (isInitialLoad) {
        setInitialLoading(false);
      }
    }
  }, [dadosTeste, filaMudou, configuracoes.somAtivado, configuracoes.somSelecionado, falarSenha]);

  // A API é a fonte canônica do estado; o Socket apenas antecipa a atualização.
  useEffect(() => {
    carregarDados(true);
  }, [carregarDados]);

  useEffect(() => {
    if (!socket || dadosTeste) return undefined;
    let inscrito = false;
    let cancelado = false;

    const assinarPainel = () => {
      socket.emit('painel:assinar', (resultado) => {
        if (cancelado) return;
        if (!resultado?.ok) {
          console.error('Não foi possível assinar as atualizações do painel:', resultado?.error);
          return;
        }
        inscrito = true;
        carregarDados(true);
      });
    };

    const atualizar = (evento = {}) => {
      // O polling pode enxergar o status já confirmado no banco antes de o
      // evento Socket chegar. Nesse caso o ID estaria no cache e a chamada não
      // seria anunciada. O evento de "Chamando" é a confirmação explícita do
      // backend e deve sempre tornar esse atendimento elegível para a locução.
      if (
        evento.atendimentoId
        && (evento.status === 'Chamando' || evento.tipo === 'atendimento-rechamado')
      ) {
        chamadasAtivasAnteriorRef.current = chamadasAtivasAnteriorRef.current
          .filter((id) => String(id) !== String(evento.atendimentoId));
      }
      carregarDados(false);
    };

    if (socket.connected) assinarPainel();
    socket.on('connect', assinarPainel);
    socket.on('painel:estado-atualizado', atualizar);
    const fallback = setTimeout(() => {
      if (!inscrito && !cancelado) carregarDados(false);
    }, 3000);
    return () => {
      cancelado = true;
      clearTimeout(fallback);
      if (socket.connected) socket.emit('painel:cancelar');
      socket.off('connect', assinarPainel);
      socket.off('painel:estado-atualizado', atualizar);
    };
  }, [socket, dadosTeste, carregarDados]);

  useEffect(() => {
    if (!socket) return undefined;
    const atualizarConfiguracoes = async () => {
      try {
        const response = await api.get('/painel/configuracoes');
        if (response.data.success && response.data.data) {
          setConfiguracoes((anterior) => ({ ...anterior, ...response.data.data }));
        }
      } catch (error) {
        console.error('Erro ao atualizar configurações do painel:', error);
      }
    };
    socket.on('painel:configuracoes-atualizadas', atualizarConfiguracoes);
    return () => socket.off('painel:configuracoes-atualizadas', atualizarConfiguracoes);
  }, [socket]);

  // Auto-refresh baseado na configuração (sem mostrar loading)
  useEffect(() => {
    if (dadosTeste || !configuracoes.autoRefresh) return;
    
    const interval = setInterval(() => {
      if (document.hidden) return;
      carregarDados(false); // false = não mostrar loading
      setDataHoraAtual(new Date());
    }, (configuracoes.refreshInterval || 15) * 1000); // Usar intervalo da configuração

    return () => clearInterval(interval);
  }, [dadosTeste, carregarDados, configuracoes.autoRefresh, configuracoes.refreshInterval]);

  useEffect(() => {
    const aoAlterarVisibilidade = () => {
      if (!document.hidden) carregarDados(false);
    };
    document.addEventListener('visibilitychange', aoAlterarVisibilidade);
    return () => document.removeEventListener('visibilitychange', aoAlterarVisibilidade);
  }, [carregarDados]);

  // Atualizar hora a cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Solicita o carregamento das vozes na inicialização. A seleção efetiva e
  // obrigatoriamente pt-BR acontece em `falarSenha`.
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const carregarVozes = () => window.speechSynthesis.getVoices();
    carregarVozes();
    window.speechSynthesis.addEventListener('voiceschanged', carregarVozes);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', carregarVozes);
    };
  }, []);

  // Ajustar estilos quando em popup
  useEffect(() => {
    const isPopup = window.opener !== null || window.name === 'painelDetalhes';
    
    if (isPopup) {
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.margin = '0';
      document.documentElement.style.padding = '0';
      document.documentElement.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
    }
  }, []);

  // Função auxiliar para buscar dados
  const buscarCliente = (id) => clientes.find(c => c.id === id || c._id === id);
  const buscarColaborador = (id) => colaboradores.find(c => c.id === id || c._id === id);
  const buscarServico = (id) => servicos.find(s => s.id === id || s._id === id);
  const buscarLocal = (id) => locais.find(l => l.id === id || l._id === id);

  // Próximos pacientes (da fila Redis ou atendimentos aguardando)
  // Buscar senha e cliente do Redis, ordenado do mais velho para o mais novo (FIFO)
  // Usar useMemo para evitar recálculos e manter referência estável
  const proximosPacientes = useMemo(() => {
    if (filaAtendimento.length > 0) {
      return filaAtendimento.slice(0, 5).map((item, index) => {
        const senha = item?.senha || null;
        const cliente = item?.cliente || 'N/A';
        
        return {
          senha: senha,
          cliente: cliente,
          colaborador: item?.colaborador || null,
          local: item?.local || null,
          posicaoNaFila: item?.posicaoNaFila || null,
          timestamp: item?.timestamp || null,
          key: item?.key || `proximo-${senha || index}-${item?.atendimentoId || index}`
        };
      });
    } else {
      // Se não houver fila Redis, buscar dos atendimentos aguardando
      const atendimentosAguardando = atendimentos
        .filter(a => a.status === 'Aguardando' && eDoDiaAtual(a.dataHoraInicio))
        .sort((a, b) => {
          const dataA = a.dataHoraInicio ? new Date(a.dataHoraInicio) : new Date(0);
          const dataB = b.dataHoraInicio ? new Date(b.dataHoraInicio) : new Date(0);
          return dataA - dataB;
        })
        .slice(0, 5)
        .map((atendimento, index) => {
          const clienteObj = buscarCliente(atendimento.clienteId);
          const clienteNome = clienteObj?.nome_completo || clienteObj?.st_nome || clienteObj?.nome || 'N/A';
          
          return {
            senha: atendimento.ordem_chegada || null,
            cliente: clienteNome,
            colaborador: null,
            timestamp: atendimento.dataHoraInicio || null,
            key: `atendimento-${atendimento._id || atendimento.id}-${index}`
          };
        });
      
      return atendimentosAguardando;
    }
  }, [filaAtendimento, atendimentos]);

  // Contar atendimentos de hoje
  const atendidosHoje = atendimentos.filter(a => {
    if (!a.dataHoraInicio) return false;
    const data = parseISO(a.dataHoraInicio);
    return isToday(data) && a.status === 'Atendido';
  }).length;

  // Últimas 3 chamadas (atendimentos concluídos, ordenados por data mais recente)
  const ultimasChamadas = useMemo(() => {
    const atendimentosConcluidos = atendimentos
      .filter(a => a.status === 'Atendido' && eDoDiaAtual(a.dataHoraInicio))
      .sort((a, b) => {
        const dataA = new Date(a.dataHoraInicio);
        const dataB = new Date(b.dataHoraInicio);
        return dataB - dataA; // Mais recente primeiro
      })
      .slice(0, 3); // Pegar apenas os 3 últimos

    return atendimentosConcluidos.map(atendimento => {
      const agendamento = agendamentos.find(a => a._id === atendimento.agendamentoId);
      const cliente = buscarCliente(atendimento.clienteId);
      const local = agendamento?.localId 
        ? buscarLocal(agendamento.localId)
        : agendamento?.local_execucao?.local 
        ? buscarLocal(agendamento.local_execucao.local)
        : null;
      const servico = agendamento?.servicoId ? buscarServico(agendamento.servicoId) : null;

      return {
        senha: atendimento.ordem_chegada || null,
        nome: cliente?.nome_completo || cliente?.nome || 'N/A',
        local: local?.nome || 'N/A',
        servico: servico?.nome || 'N/A'
      };
    });
  }, [atendimentos, agendamentos, clientes, locais, servicos]);

  return (
    <AcessoRestrito modulo="painelatendimento">
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f5f5f5',
          margin: 0,
          padding: 0,
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999
        }}
      >
        {/* Conteúdo Principal - Apenas área do dashboard */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            padding: '20px',
            gap: '20px',
            overflow: 'hidden',
            width: '100%',
            height: '100%'
          }}
        >
          {/* Coluna Esquerda (mais larga) */}
          <Box sx={{ flex: '2', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Card Informações Importantes */}
            <Card
              sx={{
                backgroundColor: '#1976d2',
                color: 'white',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 32 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                  {configuracoes.textoTitulo || 'Painel de Atendimento'}
                </Typography>
                {configuracoes.textoSubtitulo && (
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    {configuracoes.textoSubtitulo}
                  </Typography>
                )}
              </Box>
            </Card>

            {/* Player de Vídeo */}
            {configuracoes.videoUrl && (
              <Card
                sx={{
                  flex: 1,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  position: 'relative',
                  backgroundColor: '#000',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  minHeight: '400px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    paddingTop: '56.25%', // 16:9 aspect ratio
                    '& iframe': {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }
                  }}
                >
                  {(() => {
                    const video = normalizarUrlVideo(configuracoes.videoUrl);

                    if (video.tipo === 'invalido') {
                      return (
                        <Box
                          sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center', color: 'white' }}
                        >
                          <Typography>{video.mensagem}</Typography>
                        </Box>
                      );
                    }

                    return video.tipo === 'youtube' ? (
                      <>
                        <iframe
                          src={video.url}
                          title="Vídeo Informativo"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none'
                          }}
                        />
                        <Box
                          component="a"
                          href={video.urlOriginal}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            position: 'absolute',
                            right: 12,
                            bottom: 12,
                            zIndex: 1,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1,
                            color: 'white',
                            backgroundColor: 'rgba(0, 0, 0, 0.65)',
                            fontSize: '0.75rem',
                            textDecoration: 'none',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.85)' }
                          }}
                        >
                          Abrir no YouTube
                        </Box>
                      </>
                    ) : (
                    <video
                      src={video.url}
                      autoPlay
                      loop
                      muted
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    );
                  })()}
                </Box>
              </Card>
            )}

            {/* Cards Informativos (3 cards horizontais) */}
            <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
              <Grid item xs={4} sx={{ flex: 1, minWidth: 0 }}>
                <Card
                  sx={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    height: '100%',
                    width: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <VolumeUpIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                      Atenção Pacientes
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', opacity: 0.95 }}>
                    Aguarde ser chamado pelo seu nome e senha
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4} sx={{ flex: 1, minWidth: 0 }}>
                <Card
                  sx={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    height: '100%',
                    width: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WashIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                      Higienização
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', opacity: 0.95 }}>
                    Álcool gel disponível na recepção
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4} sx={{ flex: 1, minWidth: 0 }}>
                <Card
                  sx={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    height: '100%',
                    width: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FaceMaskIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                      Proteção
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', opacity: 0.95 }}>
                    Mantenha o distanciamento social
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Coluna Direita (mais estreita) */}
          <Box sx={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '20px', minWidth: '350px' }}>
            {/* Card Chamadas Ativas */}
            <Card
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              {/* Header verde */}
              <Box
                sx={{
                  backgroundColor: '#2e7d32',
                  color: 'white',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                  Chamadas Ativas
                </Typography>
              </Box>

              {/* Conteúdo do paciente */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  padding: '24px',
                  textAlign: 'center',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                {initialLoading ? (
                  <CircularProgress />
                ) : atendimentosAtivos.length > 0 ? (
                  <Grid container spacing={2}>
                    {atendimentosAtivos.map((pacienteAtual) => (
                      <Grid item xs={12} sm={atendimentosAtivos.length > 1 ? 6 : 12} key={pacienteAtual.id}>
                        <Fade in={true} timeout={500}>
                          <Box>
                      {(() => {
                        const senhaExibir = pacienteAtual?.senha || null;
                        const clienteExibir = pacienteAtual?.cliente || 'N/A';
                        const localExibir = pacienteAtual?.local || null;
                        const colaboradorExibir = pacienteAtual?.colaborador || null;
                        
                        if (!senhaExibir) {
                          console.warn('⚠️ Senha não encontrada para o paciente chamado:', { pacienteAtual });
                        }

                        return (
                          <>
                            {/* Número da senha */}
                            {senhaExibir ? (
                              <Box
                                sx={{
                                  width: 120,
                                  height: 120,
                                  borderRadius: '50%',
                                  backgroundColor: '#c8e6c9',
                                  margin: '0 auto 16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease-in-out',
                                  '&:hover': {
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              >
                                <Typography
                                  key={`senha-${senhaExibir}`}
                                  variant="h3"
                                  fontWeight="bold"
                                  sx={{ 
                                    color: '#2e7d32', 
                                    fontSize: '2.5rem',
                                    transition: 'opacity 0.3s ease-in-out'
                                  }}
                                >
                                  {senhaExibir}
                                </Typography>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: 120,
                                  height: 120,
                                  borderRadius: '50%',
                                  backgroundColor: '#ffccbc',
                                  margin: '0 auto 16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease-in-out'
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ 
                                    color: '#d32f2f', 
                                    fontSize: '0.9rem',
                                    textAlign: 'center'
                                  }}
                                >
                                  Senha<br/>não<br/>encontrada
                                </Typography>
                              </Box>
                            )}

                            {/* Nome do cliente */}
                            <Typography
                              key={`cliente-${clienteExibir}`}
                              variant="h5"
                              fontWeight="bold"
                              sx={{ 
                                mb: 1, 
                                color: '#212121', 
                                fontSize: '1.5rem',
                                transition: 'opacity 0.3s ease-in-out'
                              }}
                            >
                              {clienteExibir}
                            </Typography>

                            {localExibir && (
                              <Typography
                                key={`local-${localExibir}`}
                                variant="body1"
                                fontWeight="bold"
                                sx={{ 
                                  mb: 1,
                                  color: '#666', 
                                  fontSize: '1rem',
                                  transition: 'opacity 0.3s ease-in-out'
                                }}
                              >
                                {localExibir}
                              </Typography>
                            )}

                            {colaboradorExibir && (
                              <Typography
                                key={`colaborador-${colaboradorExibir}`}
                                variant="body1"
                                sx={{
                                  mb: 2,
                                  color: '#666',
                                  fontSize: '1rem',
                                  transition: 'opacity 0.3s ease-in-out'
                                }}
                              >
                                Atendimento: {colaboradorExibir}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                          </Box>
                        </Fade>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Nenhum paciente em chamada no momento
                  </Typography>
                )}
              </Box>
            </Card>

            {/* Card Próximos */}
            <Card
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                flex: 1
              }}
            >
              {/* Header cinza */}
              <Box
                sx={{
                  backgroundColor: '#616161',
                  color: 'white',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <ScheduleIcon sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                  Próximos
                </Typography>
              </Box>

              {/* Lista de próximos */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  padding: '16px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  transition: 'opacity 0.3s ease-in-out'
                }}
              >
                {initialLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : proximosPacientes.length > 0 ? (
                  <Fade in={true} timeout={500}>
                    <List sx={{ padding: 0 }}>
                      {proximosPacientes.map((item, index) => {
                        // Exibir senha (ordem_chegada do atendimento) e cliente
                        // item.senha já contém ordem_chegada do atendimento (definido em proximosPacientes)
                        const senhaExibir = item.senha || null;
                        const clienteExibir = item.cliente || 'N/A';
                        const contextoExibir = [item.colaborador, item.local].filter(Boolean).join(' — ');
                        
                        return (
                          <Fade 
                            key={item.key || `proximo-${senhaExibir}-${index}`}
                            in={true} 
                            timeout={300 + (index * 50)}
                            style={{ transitionDelay: `${index * 50}ms` }}
                          >
                            <Box>
                              <ListItem
                                sx={{
                                  padding: '12px 0',
                                  alignItems: 'flex-start',
                                  transition: 'background-color 0.2s ease-in-out',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                                    borderRadius: '8px'
                                  }
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    {senhaExibir ? (
                                      <Typography
                                        key={`proximo-senha-${senhaExibir}-${index}`}
                                        variant="body1"
                                        fontWeight="bold"
                                        sx={{ 
                                          color: '#1976d2', 
                                          fontSize: '1.1rem',
                                          transition: 'all 0.3s ease-in-out'
                                        }}
                                      >
                                        {senhaExibir}
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{ 
                                          color: '#d32f2f', 
                                          fontSize: '0.9rem',
                                          fontStyle: 'italic'
                                        }}
                                      >
                                        N/A
                                      </Typography>
                                    )}
                                    <Typography
                                      key={`proximo-cliente-${clienteExibir}-${index}`}
                                      variant="body1"
                                      sx={{ 
                                        flex: 1, 
                                        fontSize: '1rem',
                                        transition: 'all 0.3s ease-in-out'
                                      }}
                                    >
                                      {clienteExibir}
                                    </Typography>
                                  </Box>
                                  {contextoExibir && (
                                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                      {contextoExibir}
                                    </Typography>
                                  )}
                                </Box>
                              </ListItem>
                              {index < proximosPacientes.length - 1 && (
                                <Divider 
                                  sx={{ 
                                    transition: 'opacity 0.3s ease-in-out',
                                    marginLeft: '12px',
                                    marginRight: '12px'
                                  }} 
                                />
                              )}
                            </Box>
                          </Fade>
                        );
                      })}
                    </List>
                  </Fade>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Nenhum paciente aguardando
                  </Typography>
                )}
              </Box>
            </Card>

            {/* Card Últimas Chamadas */}
            {ultimasChamadas.length > 0 && (
            <Card
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'white',
                  padding: '16px'
                }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#212121' }}>
                  Últimas chamadas
                </Typography>
                {ultimasChamadas.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {ultimasChamadas.map((chamada, index) => (
                      <ListItem
                        key={`chamada-${index}`}
                        sx={{
                          borderBottom: index < ultimasChamadas.length - 1 ? '1px solid #e0e0e0' : 'none',
                          py: 1.5,
                          px: 0
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#212121', fontSize: '0.9rem' }}>
                          {chamada.senha || 'N/A'} - {chamada.nome} - {chamada.local} - {chamada.servico}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Nenhuma chamada registrada
                  </Typography>
                )}
              </Box>
            </Card>
            )}
          </Box>
        </Box>
      </Box>
    </AcessoRestrito>
  );
}

export default PainelDetalhes;
